import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { normalizeTimeToHHMMSS } from './time';
import { ON_CALL_NP_SCHEDULE_CATEGORY } from './codings';
import { Location, Practitioner, Reference, Schedule, Slot } from '@medplum/fhirtypes';
import { createReference, getExtension, getReferenceString, MedplumClient } from '@medplum/core';
import {
  BARIENDO_CT_SYSTEM,
  LocationType,
  ON_CALL_POLICY_TIMEZONE,
  ON_CALL_SCHEDULE_MONTHS_AHEAD,
  ON_CALL_WEEKDAY_DEFAULT_END_TIME,
  ON_CALL_WEEKDAY_DEFAULT_START_TIME,
  ON_CALL_WEEKEND_DEFAULT_END_TIME,
  ON_CALL_WEEKEND_DEFAULT_START_TIME,
  ScheduleCategory,
} from './constants';
import {
  ON_CALL_BACKUP_PRACTITIONER_EXTENSION,
  ON_CALL_PRIMARY_PRACTITIONER_EXTENSION,
  TIMEZONE_EXTENSION,
} from './extensions';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface OnCallSlotData {
  slotId?: string;
  rowDate: string;
  primaryRef?: Reference<Practitioner>;
  backupRef?: Reference<Practitioner>;
  startTime: string;
  endTime: string;
}

export interface OnCallScheduleWindow {
  dates: string[];
  rangeStart: string;
  rangeEnd: string;
}

export interface OnCallScheduleResources {
  /** Undefined when no on-call Schedule exists and the caller may not create one. */
  schedule: Schedule | undefined;
  timezone: string;
  slotsByDate: Map<string, Slot>;
}

export interface ActiveOnCallSlot {
  primaryRef: Reference<Practitioner>;
  backupRef?: Reference<Practitioner>;
}

export function normalizeTimeToHHMM(time: string): string {
  const parts = time.split(':');
  const hours = parts[0]?.padStart(2, '0') ?? '00';
  const minutes = parts[1]?.padStart(2, '0') ?? '00';
  return `${hours}:${minutes}`;
}

/**
 * The editable window: from today through the end of the month `monthsAhead` later.
 *
 * Deliberately starts today rather than on the 1st — the rest of the current month plus two
 * whole months. Rows for days already gone are not schedulable, and the stats panel is
 * where past months are looked at.
 */
export function getOnCallScheduleWindow(
  timezoneValue: string,
  monthsAhead: number = ON_CALL_SCHEDULE_MONTHS_AHEAD,
  fromDate?: string
): OnCallScheduleWindow {
  const start = dayjs.tz(fromDate ?? dayjs().tz(timezoneValue).format('YYYY-MM-DD'), timezoneValue).startOf('day');

  // Compared as formatted dates, not with dayjs day granularity. endOf('month') returns the
  // last instant of the month in `timezoneValue`, which falls on the *next* calendar day in
  // UTC — so a day-granularity isAfter() lets one extra date through.
  const endDate = start.add(monthsAhead, 'month').endOf('month').format('YYYY-MM-DD');

  const dates: string[] = [];
  for (let cursor = start; cursor.format('YYYY-MM-DD') <= endDate; cursor = cursor.add(1, 'day')) {
    dates.push(cursor.format('YYYY-MM-DD'));
  }

  return { dates, rangeStart: dates[0]!, rangeEnd: dates[dates.length - 1]! };
}

/**
 * The default shift for a date, expressed in the viewer's timezone.
 *
 * The policy is stated in Eastern — weekends run 08:30 to 08:30, weekdays 18:00 to 08:30 —
 * so the times are built as Eastern wall-clock on the relevant dates and then converted.
 * Doing it per date matters: Eastern and the viewer's zone do not always change over DST on
 * the same day, so a fixed offset would be wrong twice a year.
 */
export function getDefaultOnCallTimes(
  rowDate: string,
  displayTimezone: string
): { startTime: string; endTime: string } {
  const isWeekend = [0, 6].includes(dayjs(rowDate).day());
  const startPolicy = isWeekend ? ON_CALL_WEEKEND_DEFAULT_START_TIME : ON_CALL_WEEKDAY_DEFAULT_START_TIME;
  const endPolicy = isWeekend ? ON_CALL_WEEKEND_DEFAULT_END_TIME : ON_CALL_WEEKDAY_DEFAULT_END_TIME;

  // Both shapes end the following morning, so the end is resolved against the next date.
  const nextDate = dayjs(rowDate).add(1, 'day').format('YYYY-MM-DD');
  const start = dayjs.tz(`${rowDate}T${normalizeTimeToHHMMSS(startPolicy)}`, ON_CALL_POLICY_TIMEZONE);
  const end = dayjs.tz(`${nextDate}T${normalizeTimeToHHMMSS(endPolicy)}`, ON_CALL_POLICY_TIMEZONE);

  return {
    startTime: start.tz(displayTimezone).format('HH:mm'),
    endTime: end.tz(displayTimezone).format('HH:mm'),
  };
}

export function buildSlotInstants(
  rowDate: string,
  startTime: string,
  endTime: string,
  timezoneValue: string
): { start: string; end: string } {
  const startHHMMSS = normalizeTimeToHHMMSS(startTime);
  const endHHMMSS = normalizeTimeToHHMMSS(endTime);

  const startInstant = dayjs.tz(`${rowDate}T${startHHMMSS}`, timezoneValue).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

  const endDate =
    endHHMMSS <= startHHMMSS ? dayjs.tz(rowDate, timezoneValue).add(1, 'day').format('YYYY-MM-DD') : rowDate;
  const endInstant = dayjs.tz(`${endDate}T${endHHMMSS}`, timezoneValue).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

  return { start: startInstant, end: endInstant };
}

export function getOnCallEndDateLabel(rowDate: string, startTime: string, endTime: string): string {
  const startHHMMSS = normalizeTimeToHHMMSS(startTime);
  const endHHMMSS = normalizeTimeToHHMMSS(endTime);
  const endDate = endHHMMSS <= startHHMMSS ? dayjs(rowDate).add(1, 'day').format('YYYY-MM-DD') : rowDate;
  return dayjs(endDate).format('MMM D');
}

export function parseOnCallSlot(slot: Slot, timezoneValue: string): OnCallSlotData {
  const startLocal = dayjs(slot.start).tz(timezoneValue);
  const endLocal = dayjs(slot.end).tz(timezoneValue);
  const primaryExtension = getExtension(slot, ON_CALL_PRIMARY_PRACTITIONER_EXTENSION);
  const backupExtension = getExtension(slot, ON_CALL_BACKUP_PRACTITIONER_EXTENSION);

  return {
    slotId: slot.id,
    rowDate: startLocal.format('YYYY-MM-DD'),
    primaryRef: primaryExtension?.valueReference as Reference<Practitioner> | undefined,
    backupRef: backupExtension?.valueReference as Reference<Practitioner> | undefined,
    startTime: startLocal.format('HH:mm'),
    endTime: endLocal.format('HH:mm'),
  };
}

export function buildOnCallSlot(
  schedule: Schedule,
  rowDate: string,
  primaryRef: Reference<Practitioner>,
  backupRef: Reference<Practitioner> | undefined,
  startTime: string,
  endTime: string,
  timezoneValue: string,
  existingSlot?: Slot
): Slot {
  const { start, end } = buildSlotInstants(rowDate, startTime, endTime, timezoneValue);

  const extensions = [
    {
      url: ON_CALL_PRIMARY_PRACTITIONER_EXTENSION,
      valueReference: primaryRef,
    },
    ...(backupRef
      ? [
          {
            url: ON_CALL_BACKUP_PRACTITIONER_EXTENSION,
            valueReference: backupRef,
          },
        ]
      : []),
  ];

  return {
    ...(existingSlot ?? {}),
    resourceType: 'Slot',
    schedule: createReference(schedule),
    status: 'busy',
    start,
    end,
    extension: extensions,
  };
}

async function fetchHeadquartersLocation(medplum: MedplumClient): Promise<Location | undefined> {
  return medplum.searchOne('Location', {
    type: `${BARIENDO_CT_SYSTEM}|${LocationType.HEADQUARTERS}`,
  });
}

async function findOnCallNpSchedule(medplum: MedplumClient): Promise<Schedule | undefined> {
  return medplum.searchOne('Schedule', {
    'actor:Location.type': `${BARIENDO_CT_SYSTEM}|${LocationType.HEADQUARTERS}`,
    'service-category': `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.ON_CALL_NP}`,
  });
}

function resolveScheduleTimezone(schedule: Schedule | undefined, location: Location | undefined): string {
  const scheduleTimezone = schedule
    ? (getExtension(schedule, TIMEZONE_EXTENSION)?.valueCode as string | undefined)
    : undefined;
  const locationTimezone = location
    ? (getExtension(location, TIMEZONE_EXTENSION)?.valueString as string | undefined)
    : undefined;

  return scheduleTimezone ?? locationTimezone ?? 'America/New_York';
}

export async function ensureOnCallSchedule(
  medplum: MedplumClient,
  displayTimezone?: string,
  options: { createIfMissing?: boolean } = {}
): Promise<OnCallScheduleResources> {
  const { createIfMissing = true } = options;
  const [existingSchedule, headquartersLocation] = await Promise.all([
    findOnCallNpSchedule(medplum),
    fetchHeadquartersLocation(medplum),
  ]);

  const timezoneValue = resolveScheduleTimezone(existingSchedule, headquartersLocation);
  const effectiveTimezone = displayTimezone ?? timezoneValue;

  let schedule = existingSchedule;
  if (!schedule) {
    // Callers who cannot write must not create it as a side effect of reading. Viewers hold
    // readonly Schedule through CompartmentAccessPolicy, so the create would 403 and the
    // page would report an error instead of an empty schedule.
    if (!createIfMissing) {
      return buildEmptyOnCallResources(effectiveTimezone);
    }

    if (!headquartersLocation) {
      throw new Error('Headquarters location not found');
    }

    schedule = await medplum.createResource<Schedule>({
      resourceType: 'Schedule',
      active: true,
      actor: [createReference(headquartersLocation)],
      serviceCategory: [{ coding: [ON_CALL_NP_SCHEDULE_CATEGORY] }],
      extension: [
        {
          url: TIMEZONE_EXTENSION,
          valueCode: timezoneValue,
        },
      ],
    });
  }

  const { rangeStart, rangeEnd } = getOnCallScheduleWindow(effectiveTimezone);

  const windowStartMs = dayjs.tz(rangeStart, effectiveTimezone).startOf('day').valueOf();
  const windowEndMs = dayjs.tz(rangeEnd, effectiveTimezone).endOf('day').valueOf();

  // Paged and lower-bounded deliberately, matching findActiveOnCallSlot below. An
  // unqualified Slot search returns a single default-sized page, so a schedule holding more
  // slots than that silently loses rows — and since the editor reads a missing slot as
  // "nobody assigned", editing such a row would create a second Slot for a date that
  // already had one. The lower bound also stops the query dragging in every past shift as
  // the schedule accumulates history; the loop below still decides window membership.
  const slots = await medplum.searchResources('Slot', {
    schedule: getReferenceString(schedule),
    _count: 1000,
    _sort: 'start',
    start: `ge${dayjs(windowStartMs).subtract(1, 'day').toISOString()}`,
  });

  const slotsByDate = new Map<string, Slot>();

  for (const slot of slots) {
    if (!slot.start) {
      continue;
    }

    const slotStartMs = dayjs(slot.start).valueOf();
    if (slotStartMs < windowStartMs || slotStartMs > windowEndMs) {
      continue;
    }

    const parsed = parseOnCallSlot(slot, effectiveTimezone);
    slotsByDate.set(parsed.rowDate, slot);
  }

  return {
    schedule,
    timezone: effectiveTimezone,
    slotsByDate,
  };
}

/**
 * What a viewer gets in an environment where no on-call Schedule has been created yet.
 *
 * `schedule` is left undefined rather than faked: a stand-in object would satisfy the type
 * while producing a `Schedule/undefined` reference the moment anything tried to build a
 * Slot against it.
 */
function buildEmptyOnCallResources(timezone: string): OnCallScheduleResources {
  return {
    schedule: undefined,
    timezone,
    slotsByDate: new Map(),
  };
}

export async function fetchOnCallSchedule(
  medplum: MedplumClient,
  displayTimezone?: string,
  options: { createIfMissing?: boolean } = {}
): Promise<OnCallScheduleResources> {
  return ensureOnCallSchedule(medplum, displayTimezone, options);
}

export interface OnCallMonthOption {
  /** First day of the month, YYYY-MM-DD. */
  value: string;
  label: string;
}

export interface OnCallPractitionerHours {
  practitionerRef: Reference<Practitioner>;
  hours: number;
}

/**
 * Months offered by the stats picker, oldest first, with the current month in the middle.
 */
export function getOnCallStatsMonths(
  timezoneValue: string,
  monthsBack: number,
  monthsForward: number,
  fromDate?: string
): OnCallMonthOption[] {
  const current = dayjs.tz(fromDate ?? dayjs().tz(timezoneValue).format('YYYY-MM-DD'), timezoneValue).startOf('month');

  return Array.from({ length: monthsBack + monthsForward + 1 }, (_, index) => {
    const month = current.add(index - monthsBack, 'month');
    return { value: month.format('YYYY-MM-DD'), label: month.format('MMMM YYYY') };
  });
}

/**
 * Hours on call per primary practitioner for the month containing `monthStart`.
 *
 * A shift is counted whole against the month it starts in rather than split across the
 * boundary. An overnight shift on the last of the month would otherwise contribute a few
 * hours to the next month, which reads as noise in a staffing total nobody would reconcile.
 * Backup assignments are not counted — being backup is not being on call.
 */
export function summariseOnCallHours(
  slots: Slot[],
  monthStart: string,
  timezoneValue: string
): OnCallPractitionerHours[] {
  const start = dayjs.tz(monthStart, timezoneValue).startOf('month');
  const end = start.endOf('month');
  const totals = new Map<string, { practitionerRef: Reference<Practitioner>; hours: number }>();

  for (const slot of slots) {
    if (!slot.start || !slot.end) {
      continue;
    }

    const slotStart = dayjs(slot.start).tz(timezoneValue);
    if (slotStart.isBefore(start) || slotStart.isAfter(end)) {
      continue;
    }

    const primaryRef = getExtension(slot, ON_CALL_PRIMARY_PRACTITIONER_EXTENSION)?.valueReference as
      | Reference<Practitioner>
      | undefined;

    if (!primaryRef?.reference) {
      continue;
    }

    const hours = dayjs(slot.end).diff(dayjs(slot.start), 'minute') / 60;
    const existing = totals.get(primaryRef.reference);

    if (existing) {
      existing.hours += hours;
    } else {
      totals.set(primaryRef.reference, { practitionerRef: primaryRef, hours });
    }
  }

  return [...totals.values()].sort((a, b) => b.hours - a.hours);
}

/**
 * Every on-call slot starting in the given month, paged and bounded like the editor's.
 *
 * Takes the Schedule rather than looking it up, so a caller that already holds one does not
 * repeat that search on every month change.
 */
export async function fetchOnCallSlotsForMonth(
  medplum: MedplumClient,
  schedule: Schedule,
  monthStart: string,
  timezoneValue: string
): Promise<Slot[]> {
  const start = dayjs.tz(monthStart, timezoneValue).startOf('month');
  const end = start.endOf('month');

  const slots = await medplum.searchResources('Slot', {
    schedule: getReferenceString(schedule),
    _count: 1000,
    _sort: 'start',
    start: `ge${start.toISOString()}`,
  });

  return slots.filter((slot) => slot.start && !dayjs(slot.start).isAfter(end));
}

/**
 * Resolves the practitioners a set of slots refers to.
 *
 * Slots store bare references with no display, and the roster of currently eligible
 * practitioners is the wrong source for historical months: anyone who has since changed
 * role or left is absent from it, and would be reported as unknown in exactly the months
 * the stats panel exists to show.
 */
export async function fetchPractitionersByIds(
  medplum: MedplumClient,
  ids: string[]
): Promise<Map<string, Practitioner>> {
  const unique = [...new Set(ids)].filter(Boolean);

  if (unique.length === 0) {
    return new Map();
  }

  const practitioners = await medplum.searchResources('Practitioner', {
    _id: unique.join(','),
    _count: 1000,
  });

  return new Map(
    practitioners.filter((practitioner) => practitioner.id).map((practitioner) => [practitioner.id!, practitioner])
  );
}

export function matchActiveOnCallSlot(slots: Slot[], nowMs: number = dayjs().valueOf()): ActiveOnCallSlot | undefined {
  for (const slot of slots) {
    if (!slot.start || !slot.end) {
      continue;
    }

    const startMs = dayjs(slot.start).valueOf();
    const endMs = dayjs(slot.end).valueOf();

    if (nowMs < startMs || nowMs > endMs) {
      continue;
    }

    const primaryExtension = getExtension(slot, ON_CALL_PRIMARY_PRACTITIONER_EXTENSION);
    const primaryRef = primaryExtension?.valueReference as Reference<Practitioner> | undefined;
    if (!primaryRef) {
      return undefined;
    }

    const backupExtension = getExtension(slot, ON_CALL_BACKUP_PRACTITIONER_EXTENSION);
    const backupRef = backupExtension?.valueReference as Reference<Practitioner> | undefined;

    return { primaryRef, backupRef };
  }

  return undefined;
}

/**
 * The on-call shift covering `atMs`, which defaults to now.
 *
 * The IVR route tester (BAR-2624) passes the moment being simulated, so "who is on call at
 * 3am on Sunday" is answerable without waiting for Sunday.
 */
export async function findActiveOnCallSlot(
  medplum: MedplumClient,
  atMs: number = dayjs().valueOf()
): Promise<ActiveOnCallSlot | undefined> {
  const schedule = await findOnCallNpSchedule(medplum);

  if (!schedule) {
    return undefined;
  }

  // subtract 1 day to avoid possible timezone issues when fetching slots
  const startFilter = dayjs(atMs).subtract(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ');

  const slots = await medplum.searchResources('Slot', {
    schedule: getReferenceString(schedule),
    _count: 1000,
    _sort: 'start',
    start: `sa${startFilter}`,
  });

  return matchActiveOnCallSlot(slots, atMs);
}
